import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useLeaveHistory } from '@/hooks/useLeaveHistory';
import { useCalendarSync } from '@/hooks/useCalendarSync';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Calendar,
  Search,
  Plus,
  Loader2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CalendarSync,
  Copy,
  Check,
  RefreshCw,
  ExternalLink,
  Shield,
  Clock,
  CalendarCheck,
  Sun,
  Palmtree,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { getRequestTypeLabel, getRequestStatusLabel } from '@/lib/attendanceTypes';
import { motion } from 'framer-motion';

// Stat pill component - matches Dashboard exactly
const StatPill = ({ icon: Icon, label, value, color = "slate", delay = 0 }) => {
  const colorClasses = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className={`flex items-center gap-3 px-4 py-3 rounded-2xl border ${colorClasses[color]} transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-default`}
    >
      <div className="p-2 rounded-xl bg-white/80 shadow-sm">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-xs font-medium opacity-70">{label}</p>
        <p className="text-lg font-bold -mt-0.5">{value}</p>
      </div>
    </motion.div>
  );
};

// Request card - clean, no motion for stable filtering
const RequestCard = ({ request, formatDate, getPartDayText, canWithdraw, onWithdraw }) => {
  const statusConfig = {
    pending: {
      accent: "bg-amber-500",
      badge: "bg-amber-50 text-amber-700 border-amber-200",
      icon: Clock
    },
    approved: {
      accent: "bg-emerald-500",
      badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
      icon: CalendarCheck
    },
    rejected: {
      accent: "bg-slate-400",
      badge: "bg-slate-100 text-slate-600 border-slate-200",
      icon: Calendar
    },
    withdrawn: {
      accent: "bg-slate-300",
      badge: "bg-slate-50 text-slate-500 border-slate-200",
      icon: Calendar
    }
  };

  const config = statusConfig[request.status] || statusConfig.pending;
  const StatusIcon = config.icon;

  return (
    <div
      className="group relative bg-white rounded-xl border border-slate-200/80
        hover:border-slate-300 hover:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.12)]
        transition-all duration-300 overflow-hidden"
    >
      {/* Status accent bar - rounded, with padding from edges */}
      <div className={`absolute left-0 top-3 bottom-3 w-1 ${config.accent} rounded-full`} />

      <div className="flex items-stretch">
        {/* Main content */}
        <div className="flex-1 py-4 pl-5 pr-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center gap-2">
              <StatusIcon className="h-4 w-4 text-slate-400" />
              <span className="font-semibold text-slate-800 text-[15px]">
                {getRequestTypeLabel(request.leave_type)}
              </span>
            </div>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${config.badge}`}>
              {getRequestStatusLabel(request.status)}
            </span>
            {request.calculated_days && (
              <span className="text-slate-400 text-xs">
                ({request.calculated_days} {request.calculated_days === 1 ? 'day' : 'days'})
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span className="font-medium tabular-nums">
              {formatDate(request.start_date)}
            </span>
            <span className="text-slate-300">→</span>
            <span className="font-medium tabular-nums">
              {formatDate(request.end_date)}
            </span>
          </div>

          {getPartDayText(request) && (
            <p className="text-xs text-slate-400 mt-1">
              Part days: {getPartDayText(request)}
            </p>
          )}

          {request.user_note && (
            <p className="text-sm text-slate-500 mt-2.5 pl-3 border-l-2 border-slate-200 line-clamp-1">
              {request.user_note}
            </p>
          )}

          {request.admin_note && request.status === 'rejected' && (
            <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-xs font-medium text-slate-500 mb-1.5">Reason for rejection</p>
              <p className="text-sm text-slate-700">{request.admin_note}</p>
            </div>
          )}

          <p className="text-xs text-slate-400 mt-3">
            Submitted {formatDate(request.created_date)}
          </p>
        </div>

        {/* Action area */}
        {canWithdraw(request) && (
          <div className="flex items-center px-4 border-l border-slate-100 group-hover:border-slate-200 transition-colors">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onWithdraw(request)}
              className="text-slate-400 hover:text-red-600 hover:bg-red-50"
            >
              <XCircle className="h-4 w-4 mr-1.5" />
              Withdraw
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const LeaveHistoryPage = () => {
  const { userProfile, can } = useAuth();
  const canManageAttendance = can('attendance.manage');

  usePageTitle('My Leave');

  const {
    requests,
    balance,
    stats,
    loading,
    pagination,
    filters,
    withdrawRequest
  } = useLeaveHistory(userProfile?.id);

  const {
    hasToken,
    httpsUrl,
    generating,
    generateToken,
    revokeToken,
    copyToClipboard,
    openSubscription
  } = useCalendarSync(userProfile?.id);

  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [requestToWithdraw, setRequestToWithdraw] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [calendarPopoverOpen, setCalendarPopoverOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);

  const formatDate = useCallback((dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }, []);

  const getPartDayText = useCallback((request) => {
    if (!request.part_day) return null;
    const parts = [];
    if (request.part_day.start) parts.push(`Start: ${request.part_day.start}`);
    if (request.part_day.end) parts.push(`End: ${request.part_day.end}`);
    return parts.join(', ');
  }, []);

  const canWithdraw = useCallback((request) => {
    if (!canManageAttendance) return false;
    if (request.status !== 'pending' && request.status !== 'approved') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(request.start_date) >= today;
  }, [canManageAttendance]);

  const openWithdrawDialog = useCallback((request) => {
    setRequestToWithdraw(request);
    setWithdrawDialogOpen(true);
  }, []);

  const handleCopyCalendarUrl = useCallback(async () => {
    const result = await copyToClipboard('https');
    if (result.success) {
      setCopied(true);
      toast.success('Calendar URL copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Failed to copy URL');
    }
  }, [copyToClipboard]);

  const handleGenerateToken = useCallback(async () => {
    const result = await generateToken();
    if (result.success) toast.success('Calendar sync enabled');
    else toast.error(result.error || 'Failed to enable calendar sync');
  }, [generateToken]);

  const handleRegenerateToken = useCallback(async () => {
    setShowRegenerateDialog(false);
    const result = await generateToken();
    if (result.success) toast.success('Calendar link regenerated');
    else toast.error(result.error || 'Failed to regenerate link');
  }, [generateToken]);

  const handleRevokeToken = useCallback(async () => {
    setShowRevokeDialog(false);
    const result = await revokeToken();
    if (result.success) {
      toast.success('Calendar sync disabled');
      setCalendarPopoverOpen(false);
    } else {
      toast.error(result.error || 'Failed to disable sync');
    }
  }, [revokeToken]);

  const handleWithdraw = async () => {
    if (!requestToWithdraw) return;
    setWithdrawing(true);

    try {
      const success = await withdrawRequest(requestToWithdraw);
      if (success) {
        try {
          const startDate = new Date(requestToWithdraw.start_date).toLocaleDateString('en-GB', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
          });
          const endDate = new Date(requestToWithdraw.end_date).toLocaleDateString('en-GB', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
          });
          await supabase.functions.invoke('process-notification', {
            body: {
              type: 'leave',
              leave_type: 'withdrawn',
              user_ids: [requestToWithdraw.user_id],
              title: 'Leave Request Withdrawn',
              message: `Your ${getRequestTypeLabel(requestToWithdraw.leave_type)} request has been withdrawn`,
              link_url: `/attendance/history`,
              entity_type: 'leave_request',
              entity_id: requestToWithdraw.id,
              actor_id: userProfile.id,
              template_data: {
                employee_name: `${userProfile.first_name} ${userProfile.last_name}`,
                leave_type: getRequestTypeLabel(requestToWithdraw.leave_type),
                start_date: startDate,
                end_date: endDate,
                total_days: requestToWithdraw.calculated_days || 0,
                withdrawn_date: new Date().toLocaleDateString('en-GB', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                }),
                app_url: window.location.origin,
                short_link: `${window.location.origin}/attendance/history`
              }
            }
          });
        } catch (notifError) {
          console.error('Failed to send withdrawal notification:', notifError);
        }
        toast.success('Leave request withdrawn');
      } else {
        toast.error('Failed to withdraw leave request');
      }
    } catch (error) {
      console.error('Failed to withdraw request:', error);
      toast.error('Failed to withdraw leave request');
    } finally {
      setWithdrawing(false);
      setWithdrawDialogOpen(false);
      setRequestToWithdraw(null);
    }
  };

  const canGoPrevious = pagination.page > 1;
  const canGoNext = pagination.page < pagination.totalPages;

  if (loading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-slate-100" />
            <motion.div
              className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-slate-500"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="text-slate-500 font-medium">Loading your leave history...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Section - matches Dashboard style */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="rounded-2xl bg-white border border-slate-200 p-8"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
              My Leave
            </h1>
            <p className="text-slate-500 mt-1">
              View and manage your leave requests
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Calendar Sync */}
            <Popover open={calendarPopoverOpen} onOpenChange={setCalendarPopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" className="h-10 w-10 rounded-full border-slate-200">
                  <CalendarSync className="h-4 w-4 text-slate-600" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0 border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <div className="flex items-center gap-2">
                    <CalendarSync className="h-4 w-4 text-slate-600" />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Calendar Sync</p>
                      <p className="text-xs text-slate-500">Sync to Outlook/Google</p>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  {!hasToken ? (
                    <div className="space-y-3">
                      <p className="text-xs text-slate-600">
                        Generate a subscription link to sync approved leave to your calendar.
                      </p>
                      <Button onClick={handleGenerateToken} disabled={generating} className="w-full bg-slate-800 hover:bg-slate-700">
                        {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : 'Enable Sync'}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Input value={httpsUrl || ''} readOnly className="font-mono text-xs bg-slate-50 h-9" />
                        <Button variant="outline" size="icon" className="h-9 w-9" onClick={handleCopyCalendarUrl}>
                          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openSubscription()} className="flex-1 h-9">
                          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Add to Calendar
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setShowRegenerateDialog(true)} disabled={generating} className="h-9 w-9 p-0">
                          <RefreshCw className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                        <Shield className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800">Keep this link private.</p>
                      </div>
                      <button onClick={() => setShowRevokeDialog(true)} className="text-xs text-red-600 hover:underline w-full text-center">
                        Disable sync
                      </button>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            <Link to="/attendance/request">
              <Button className="h-10 bg-slate-800 hover:bg-slate-700 rounded-full px-5">
                <Plus className="h-4 w-4 mr-2" />
                New Request
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid - using StatPill component */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatPill icon={Sun} label="Entitled" value={`${balance?.entitled_days || 0}`} color="indigo" delay={0.2} />
          <StatPill icon={Palmtree} label="Taken" value={`${balance?.taken_days || 0}`} color="amber" delay={0.25} />
          <StatPill icon={Sparkles} label="Remaining" value={`${balance?.remaining_days || 0}`} color="emerald" delay={0.3} />
          <StatPill icon={Clock} label="Pending" value={stats.pending} color="violet" delay={0.35} />
          <StatPill icon={CalendarCheck} label="Approved" value={stats.approved} color="slate" delay={0.4} />
          <StatPill icon={Calendar} label="Total" value={stats.total} color="slate" delay={0.45} />
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search requests..."
            value={filters.search}
            onChange={(e) => filters.setSearch(e.target.value)}
            className="pl-9 h-10 rounded-full border-slate-200 focus:border-slate-400 focus:ring-slate-400"
          />
        </div>
        <Select value={filters.status} onValueChange={filters.setStatus}>
          <SelectTrigger className="w-[140px] h-10 rounded-full border-slate-200">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="withdrawn">Withdrawn</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.type} onValueChange={filters.setType}>
          <SelectTrigger className="w-[160px] h-10 rounded-full border-slate-200">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Annual Leave">Annual Leave</SelectItem>
            <SelectItem value="Sick Leave">Sick Leave</SelectItem>
            <SelectItem value="Unpaid Leave">Unpaid Leave</SelectItem>
            <SelectItem value="Work From Home">Work From Home</SelectItem>
            <SelectItem value="Compassionate Leave">Compassionate</SelectItem>
            <SelectItem value="Parental Leave">Parental</SelectItem>
            <SelectItem value="Training">Training</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      {/* Requests List */}
      <div>
        {requests.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
            <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-slate-100 flex items-center justify-center">
              <Calendar className="h-7 w-7 text-slate-400" />
            </div>
            <p className="text-slate-700 font-semibold text-[15px]">No requests found</p>
            <p className="text-slate-500 text-sm mt-1.5 max-w-xs mx-auto">
              Try adjusting your filters or create a new request
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((request) => (
              <RequestCard
                key={request.id}
                request={request}
                formatDate={formatDate}
                getPartDayText={getPartDayText}
                canWithdraw={canWithdraw}
                onWithdraw={openWithdrawDialog}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.filteredItems > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Show</span>
            <Select value={pagination.pageSize.toString()} onValueChange={(v) => pagination.setPageSize(Number(v))}>
              <SelectTrigger className="w-[70px] h-8 rounded-lg border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <span>of {pagination.totalItems}</span>
          </div>

          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => pagination.setPage(1)} disabled={!canGoPrevious} className="h-8 w-8 p-0 rounded-full border-slate-200">
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => pagination.setPage(pagination.page - 1)} disabled={!canGoPrevious} className="h-8 w-8 p-0 rounded-full border-slate-200">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-3 text-sm text-slate-600 tabular-nums">
              {pagination.page} / {pagination.totalPages}
            </span>
            <Button variant="outline" size="sm" onClick={() => pagination.setPage(pagination.page + 1)} disabled={!canGoNext} className="h-8 w-8 p-0 rounded-full border-slate-200">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => pagination.setPage(pagination.totalPages)} disabled={!canGoNext} className="h-8 w-8 p-0 rounded-full border-slate-200">
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </motion.div>
      )}

      {/* Withdraw Dialog */}
      <AlertDialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
        <AlertDialogContent className="max-w-md p-0 overflow-hidden rounded-xl border-slate-200">
          <div className="px-6 py-5 bg-red-50">
            <h3 className="text-lg font-semibold text-slate-800">Withdraw Leave Request?</h3>
            <p className="text-sm text-slate-600 mt-1">This action cannot be undone.</p>
          </div>
          {requestToWithdraw && (
            <div className="px-6 py-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <p className="text-xs font-medium text-slate-500 mb-1">Type</p>
                  <p className="text-sm font-semibold text-slate-800">{getRequestTypeLabel(requestToWithdraw.leave_type)}</p>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-slate-500 mb-1">Dates</p>
                  <p className="text-sm text-slate-700 tabular-nums">{formatDate(requestToWithdraw.start_date)} → {formatDate(requestToWithdraw.end_date)}</p>
                </div>
              </div>
            </div>
          )}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
            <AlertDialogCancel disabled={withdrawing} className="border-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleWithdraw} disabled={withdrawing} className="bg-red-600 hover:bg-red-700 text-white">
              {withdrawing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Withdrawing...</> : 'Withdraw'}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Regenerate Dialog */}
      <AlertDialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <AlertDialogContent className="max-w-md p-0 overflow-hidden rounded-xl border-slate-200">
          <div className="px-6 py-5">
            <h3 className="text-lg font-semibold text-slate-800">Regenerate Calendar Link?</h3>
            <p className="text-sm text-slate-600 mt-1">Your old link will stop working immediately.</p>
          </div>
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
            <AlertDialogCancel className="border-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerateToken} className="bg-slate-800 hover:bg-slate-700">Regenerate</AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Dialog */}
      <AlertDialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <AlertDialogContent className="max-w-md p-0 overflow-hidden rounded-xl border-slate-200">
          <div className="px-6 py-5">
            <h3 className="text-lg font-semibold text-slate-800">Disable Calendar Sync?</h3>
            <p className="text-sm text-slate-600 mt-1">Any subscriptions using this link will stop receiving updates.</p>
          </div>
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
            <AlertDialogCancel className="border-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevokeToken} className="bg-red-600 hover:bg-red-700 text-white">Disable</AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
